import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "ticketino-api-client";
import { GetOrders, GetOrdersOfBuyer, GetPdfTicketsOfOrderByOrderId, GetTicketTypeInfosByTicketTypeId, GetReceiptOfOrderByOrderId, GetAddressOfOrder, GetTicketsOfOrder } from "../../utils/api";
import { useLanguage } from "../../hooks/useLanguage";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { useOrder } from "../../hooks/useOrder";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";

const OrderOverview = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [buyerToken, setBuyerToken] = useState(null);

    const { orderId } = useParams();

    const organizerId = localStorage.getItem("loggedInOrganizerId");
    const [order, setOrder] = useState(null); // changed from orders array to single order

    //const isAdmin = useMemo(() => organizerId === "2639969" || organizerId === "817192", [organizerId]);
    const isAdmin = useMemo(() => organizerId === "2639969", [organizerId]);

    const [organizerWOWId, setOrganizerWOWId] = useState(null);

    const [resources, setResources] = useState({});
    const { language, languageId } = useLanguage();

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const sessionOrder = useOrder(baseUrl);

    useAuthGuard(language); // Check if user is authenticated

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        setBuyerToken(localStorage.getItem("buyerToken"));

        requestFormSettings();
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (buyerToken && organizerId && orderId && organizerWOWId) {
            if (isAdmin) {
                fetchOrder();
            } else {
                fetchBuyerOrder();
            }
        }
    }, [buyerToken, organizerId, orderId, isAdmin, organizerWOWId]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            setOrganizerWOWId(res.data.organizerId);
        });
    };

    // Attach tickets and ticket type info to the order, filter info by languageId
    const attachTicketsAndAddressToOrder = async (orderObj) => {
        if (!orderObj) return null;
        try {
            const tickets = await GetTicketsOfOrder(baseUrl, "buyerToken", buyerToken, orderObj.id);
            const ticketsWithInfo = await Promise.all(
                (Array.isArray(tickets) ? tickets : []).map(async (ticket) => {
                    try {
                        const info = await GetTicketTypeInfosByTicketTypeId(baseUrl, "token", regularToken, ticket.ticketTypeId);
                        let filteredInfo = null;
                        if (info && Array.isArray(info.ticketTypeInfos)) {
                            filteredInfo = info.ticketTypeInfos.find(i => i.languageId === languageId);
                            if (!filteredInfo) {
                                filteredInfo = info.ticketTypeInfos[0] || null;
                            }
                        }
                        return { ...ticket, Info: filteredInfo };
                    } catch {
                        return { ...ticket, Info: null };
                    }
                })
            );
            const address = await GetAddressOfOrder(baseUrl, "buyerToken", buyerToken, orderObj.id);
            return { ...orderObj, tickets: ticketsWithInfo, address: address };
        } catch {
            return { ...orderObj, tickets: [], address: {} };
        }
    };

    // Only fetch the single order matching the param
    const fetchOrder = async () => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrder");
            }
            const data = await GetOrders(baseUrl, "buyerToken", buyerToken, organizerId, true, orderId);
            const foundOrder = (data.orderList && data.orderList.length > 0) ? data.orderList[0] : null;
            if (foundOrder) {
                const orderWithTickets = await attachTicketsAndAddressToOrder(foundOrder);
                setOrder(orderWithTickets);
            } else {
                setOrder(null);
            }
        } catch (error) {
            console.error('Error fetching order:', error);
        }
    };

    const fetchBuyerOrder = async () => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchBuyerOrder");
            }
            const data = await GetOrdersOfBuyer(baseUrl, "buyerToken", buyerToken, organizerId, true, orderId);
            const foundOrder = (data.orderList && data.orderList.length > 0) ? data.orderList[0] : null;
            if (foundOrder) {
                const orderWithTickets = await attachTicketsAndAddressToOrder(foundOrder);
                setOrder(orderWithTickets);
            } else {
                setOrder(null);
            }
        } catch (error) {
            console.error('Error fetching order:', error);
        }
    };

    const fetchPdfTicketsOfOrder = async (orderId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchPdfTicketsOfOrder");
            }
            const pdfBlob = await GetPdfTicketsOfOrderByOrderId(baseUrl, "buyerToken", buyerToken, orderId, organizerId);
            const data = window.URL.createObjectURL(pdfBlob);
            var link = document.createElement("a");
            link.href = data;
            link.download = "Tickets.pdf";
            link.click();
        } catch (error) {
            console.error('Error getting pdf tickets:', error);
        }
    };

    const fetchPdfReceiptOfOrder = async (orderId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchPdfReceiptOfOrder");
            }
            const pdfBlob = await GetReceiptOfOrderByOrderId(baseUrl, "buyerToken", buyerToken, orderId, organizerId);
            const data = window.URL.createObjectURL(pdfBlob);
            var link = document.createElement("a");
            link.href = data;
            link.download = "Tickets.pdf";
            link.click();
        } catch (error) {
            console.error('Error getting pdf tickets:', error);
        }
    };

    // Group tickets by ticketTypeId for the single order
    const groupedTickets = order && order.tickets ? (() => {
        const map = new Map();
        order.tickets.forEach(ticket => {
            const ticketTypeId = ticket.ticketTypeId;
            if (!map.has(ticketTypeId)) {
                map.set(ticketTypeId, {
                    name: ticket.Info?.name || '',
                    price: ticket.ticketPrice,
                    currency: ticket.currency,
                    count: 1
                });
            } else {
                map.get(ticketTypeId).count++;
            }
        });
        return Array.from(map.values());
    })() : [];

    // Helper to format date string as DD.MM.YYYY HH:mm
    function formatDate(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    return (
        <>
            <Header language={language} pagePath="login" pageTitle={resources?.Account_LoginYourAccount}
                ticketCount={sessionOrder?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* navigation */}
                    <div className="row mb-5">
                        <div className="col-6">
                            <button className="btn-custom btn-navigation" type="submit">
                                {resources?.Account_MyOrders}
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button className="btn-custom transparent btn-navigation" type="submit"
                                onClick={() => navigate(`/${language}/profile`)}>
                                {resources?.Account_MyProfile}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="container">

                    {/* Order Table */}
                    {order &&
                        <>
                            <table className="table table-no-border table-align-top">
                                <thead>
                                    <tr>
                                        <th>{resources?.Account_MyOrdersDateTime}</th>
                                        <th>{resources?.Account_MyOrdersInvoiceNumber}</th>
                                        <th>{resources?.Account_MyOrdersCurrency}</th>
                                        <th>{resources?.Account_MyOrdersTotal}</th>
                                        <th>{resources?.Account_MyOrdersIsPaid}</th>
                                        <th>{resources?.Account_MyProfileTicketsQuantity}</th>
                                        <th>{resources?.Account_MyOrdersTickets}</th>
                                        <th>{resources?.Account_MyOrdersReceipt}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr key={order.invoiceNumber}>
                                        <td>{formatDate(order.dateCreated)}</td>
                                        <td>
                                            {order.invoiceNumber}
                                            {order.isCancelled &&
                                                <p className="text-red">** {resources?._Cancelled} **</p>
                                            }
                                        </td>
                                        <td>{order.currency}</td>
                                        <td>{order.totalPrice?.toFixed(2)}</td>
                                        <td>{order.isPaid ? resources?._Yes : resources?._No}</td>
                                        <td>{order.tickets?.length ?? ''}</td>
                                        <td>
                                            <button
                                                className="btn btn-link"
                                                type="button"
                                                disabled={order.isCancelled || (!isAdmin && !order.isOneWeekBeforeEvent) || (!isAdmin && order.paymentMethodId === 2) || (!isAdmin && !order.isPaid)}
                                                onClick={() => fetchPdfTicketsOfOrder(order.id)}
                                            >
                                                {resources?._Download}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-link"
                                                type="button"
                                                disabled={order.isCancelled || (!isAdmin && !order.isOneWeekBeforeEvent) || (!isAdmin && order.paymentMethodId === 2) || (!isAdmin && !order.isPaid)}
                                                onClick={() => fetchPdfReceiptOfOrder(order.id)}
                                            >
                                                {resources?._Download}
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="row text-center mt-5">
                                <div className="col-md-12">
                                    <span className="title">{resources?.Account_OrderOverview}</span>
                                </div>
                            </div>

                            {/* map grouped Tickets */}
                            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                                <table className="table table-no-border table-align-top mt-5">
                                    <tbody>
                                        {groupedTickets.map((group, idx) => (
                                            <tr key={idx}>
                                                <td>{group.name}</td>
                                                <td>{group.count}x {group.currency} {group.price?.toFixed(2)}</td>
                                                <td>
                                                    {group.currency} {(group.count * (group.price ?? 0)).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="row text-center mt-5">
                                <div className="col-md-12">
                                    <span className="title">{resources?.Account_OrderOverviewMoreOffers}</span>
                                </div>
                            </div>

                            <div className="row text-center mt-5">
                                <div className="col-md-12">
                                    <span className="title">{resources?.Account_OrderOverviewChangeBooking}</span>
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <span dangerouslySetInnerHTML={{ __html: resources?.Account_OrderOverviewChangeBookingDesc }}></span>
                                </div>
                            </div>

                            {/* kontaktperson */}
                            {(order?.tickets[0]?.option1 || order?.tickets[0]?.option2 || order?.tickets[0]?.option3) &&
                                <div className="row text-center mt-5 pb-2">
                                    <div className="col-md-12">
                                        <span className="title">Kontaktperson</span>
                                    </div>
                                </div>
                            }

                            {/* contact person first name */}
                            {order?.tickets[0]?.option1 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Vorname Kontaktperson: <strong className="text-red">{order.tickets[0].option1}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* contact person last name */}
                            {order?.tickets[0]?.option2 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Nachname Kontaktperson: <strong className="text-red">{order.tickets[0].option2}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* contact person phone */}
                            {order?.tickets[0]?.option3 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Telefonnummer Kontaktperson: <strong className="text-red">{order.tickets[0].option3}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* Weitere Informationen */}
                            {(order?.tickets[0]?.option4 || order?.tickets[0]?.option5 || order?.tickets[0]?.option6 || order?.tickets[0]?.option7 ||
                                order?.tickets[0]?.option8 || order?.tickets[0]?.option9) &&
                                <div className="row text-center mt-5 pb-1">
                                    <div className="col-md-12">
                                        <span className="title">{resources?.Account_OrderOverviewMoreInformation}</span>
                                    </div>
                                </div>
                            }

                            {/* birthday milkshake */}
                            {order?.tickets[0]?.option5 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Anschliessendes Milkshakes geniessen im Black Tap: <strong className="text-red">{order.tickets[0].option5}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* birthday name */}
                            {order?.tickets[0]?.option6 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Name des Geburtstagskind: <strong className="text-red">{order.tickets[0].option6}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* birthday age */}
                            {order?.tickets[0]?.option7 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Alter des Geburtstagskind: <strong className="text-red">{order.tickets[0].option7}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* school grade level */}
                            {order?.tickets[0]?.option8 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Klassenstufe: <strong className="text-red">{order.tickets[0].option8}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* group description */}
                            {order?.tickets[0]?.option9 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Beschreibung der Gruppe: <strong className="text-red">{order.tickets[0].option9}</strong></span>
                                    </div>
                                </div>
                            }

                            {/* remarks */}
                            {order?.tickets[0]?.option4 &&
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <span>Bemerkung: <strong className="text-red">{order.tickets[0].option4}</strong></span>
                                    </div>
                                </div>
                            }

                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <button
                                        className="btn-custom white"
                                        type="submit"
                                        onClick={() => navigate(`/${language}/orders`)}
                                    >
                                        {resources?._Back}
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>

            <Footer language={language} />
        </>
    )
}

export default OrderOverview